import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, AlarmClock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email, and message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Message sent. We will reply soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-32 bg-[#EEF4F8]" data-testid="contact-section">
      <div className="container-pad mx-auto max-w-7xl grid lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#2A9D8F] mb-5">
            Get in touch
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0F2942] leading-[1.1]" data-testid="contact-heading">
            We’re here, day or night.
          </h2>
          <p className="mt-5 text-base md:text-lg text-[#475569] leading-relaxed max-w-md">
            Whether you’re ready to start care or just have a few questions,
            our team is one call away.
          </p>

          <div className="mt-8 space-y-4 text-[#0F2942]">
            <ContactRow icon={Phone} label="Call us" value="647-804-0263" testId="contact-phone" />
            <ContactRow icon={Mail} label="Email" value="info@julicarehealthservices.com" testId="contact-email" />
            <ContactRow icon={MapPin} label="Service area" value="Mississauga · Milton · Oakville · Burlington · Brampton" testId="contact-area" />
            <ContactRow icon={AlarmClock} label="24/7 emergency line" value="647-804-0263" testId="contact-emergency" />
          </div>

          <div className="mt-10 rounded-2xl overflow-hidden border border-[#1E5F8C]/10" data-testid="contact-map">
            <iframe
              title="Julicare service area map"
              src="https://www.google.com/maps?q=Mississauga%2C%20Ontario&output=embed"
              className="w-full h-64"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="lg:col-span-7 bg-[#F8FAFC] rounded-3xl p-6 md:p-10 border border-[#1E5F8C]/10 soft-shadow"
          data-testid="contact-form"
        >
          {done ? (
            <div className="text-center py-10" data-testid="contact-success">
              <div className="h-14 w-14 rounded-full bg-[#DCEAF2] flex items-center justify-center mx-auto">
                <CheckCircle2 size={28} className="text-[#1E5F8C]" />
              </div>
              <h3 className="font-serif text-2xl mt-5 text-[#0F2942]">Message received</h3>
              <p className="mt-3 text-[#475569] max-w-md mx-auto">
                Thank you for reaching out. We aim to respond to every message
                within one business day.
              </p>
              <button
                type="button"
                onClick={() => setDone(false)}
                className="mt-6 text-sm font-medium text-[#1E5F8C] underline-offset-4 hover:underline"
              >
                Send another message
              </button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full name *">
                <input data-testid="contact-name" required value={form.name} onChange={handle("name")} className="w-full bg-[#EEF4F8] rounded-xl px-4 py-3 border-none focus:ring-sage focus:outline-none" placeholder="Jane Doe" />
              </Field>
              <Field label="Email *">
                <input data-testid="contact-email-input" required type="email" value={form.email} onChange={handle("email")} className="w-full bg-[#EEF4F8] rounded-xl px-4 py-3 border-none focus:ring-sage focus:outline-none" placeholder="you@example.com" />
              </Field>
              <Field label="Phone">
                <input data-testid="contact-phone-input" value={form.phone} onChange={handle("phone")} className="w-full bg-[#EEF4F8] rounded-xl px-4 py-3 border-none focus:ring-sage focus:outline-none" placeholder="647-804-0263" />
              </Field>
              <Field label="Subject">
                <input data-testid="contact-subject" value={form.subject} onChange={handle("subject")} className="w-full bg-[#EEF4F8] rounded-xl px-4 py-3 border-none focus:ring-sage focus:outline-none" placeholder="How can we help?" />
              </Field>
              <Field label="Message *" className="sm:col-span-2">
                <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={handle("message")} className="w-full bg-[#EEF4F8] rounded-xl px-4 py-3 border-none focus:ring-sage focus:outline-none resize-none" placeholder="Tell us a little about who you’re caring for and what you need." />
              </Field>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={sending}
                  className="inline-flex items-center justify-center rounded-full bg-[#1E5F8C] text-[#F8FAFC] px-8 py-4 font-medium hover:bg-[#2A75AB] transition-colors disabled:opacity-60"
                  data-testid="contact-submit"
                >
                  {sending ? "Sending..." : "Send Message"}
                </button>
              </div>
            </div>
          )}
        </motion.form>
      </div>
    </section>
  );
}

function ContactRow({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-start gap-4" data-testid={testId}>
      <div className="h-11 w-11 rounded-2xl bg-[#DCEAF2] flex items-center justify-center">
        <Icon size={20} className="text-[#1E5F8C]" />
      </div>
      <div>
        <p className="text-xs uppercase tracking-widest text-[#2A9D8F] font-semibold">{label}</p>
        <p className="text-base text-[#0F2942]">{value}</p>
      </div>
    </div>
  );
}

function Field({ label, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-sm font-medium text-[#0F2942] mb-2">{label}</span>
      {children}
    </label>
  );
}
